/**
 * AddPartitionFormBody — name + boundary kind + values orchestrator for
 * `add-partition`. Boundary kinds shown depend on engine:
 *   - postgres: range-from-to / range-less-than / list-in / list-default
 *   - mysql:    range-less-than / list-in / maxvalue
 *   - oracle:   range-less-than / list-in / maxvalue / hash
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E1.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import type {
  PartitionBoundary,
  PartitionDef,
} from '../../../../sql-resolver/types/ddlEdit';
import {
  commitAddPartition,
  validateAddPartitionForEngine,
} from '../../edit/edits/addPartition';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

type BoundaryKind = PartitionBoundary['kind'];

const BOUNDARY_OPTIONS: Record<SqlEngine, ReadonlyArray<{ kind: BoundaryKind; label: string }>> = {
  mssql: [],
  postgres: [
    { kind: 'range-from-to', label: 'FOR VALUES FROM (…) TO (…)' },
    { kind: 'range-less-than', label: 'VALUES LESS THAN (…)' },
    { kind: 'list-in', label: 'FOR VALUES IN (…)' },
    { kind: 'list-default', label: 'FOR VALUES DEFAULT' },
  ],
  mysql: [
    { kind: 'range-less-than', label: 'VALUES LESS THAN (…)' },
    { kind: 'list-in', label: 'VALUES IN (…)' },
    { kind: 'maxvalue', label: 'VALUES LESS THAN (MAXVALUE)' },
  ],
  oracle: [
    { kind: 'range-less-than', label: 'VALUES LESS THAN (…)' },
    { kind: 'list-in', label: 'VALUES (…)' },
    { kind: 'maxvalue', label: 'VALUES LESS THAN (MAXVALUE)' },
    { kind: 'hash', label: 'HASH (no boundary)' },
  ],
  sqlite: [],
};

export interface AddPartitionFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly table: QualifiedName | null;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function AddPartitionFormBody(props: AddPartitionFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel, table } = props;
  const engine = editContext.service.engineId;
  const options = BOUNDARY_OPTIONS[engine];
  const [name, setName] = useState('');
  const [kind, setKind] = useState<BoundaryKind>(options[0]?.kind ?? 'maxvalue');
  const [valuesText, setValuesText] = useState('');
  const [fromText, setFromText] = useState('');
  const [toText, setToText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildBoundary(): PartitionBoundary | { error: string } {
    switch (kind) {
      case 'range-less-than':
        if (valuesText.trim().length === 0) return { error: 'Values text is required.' };
        return { kind: 'range-less-than', valuesText: valuesText.trim() };
      case 'range-from-to':
        if (fromText.trim().length === 0 || toText.trim().length === 0) {
          return { error: 'FROM and TO values are required.' };
        }
        return { kind: 'range-from-to', fromText: fromText.trim(), toText: toText.trim() };
      case 'list-in':
        if (valuesText.trim().length === 0) return { error: 'Values text is required.' };
        return { kind: 'list-in', valuesText: valuesText.trim() };
      case 'list-default': return { kind: 'list-default' };
      case 'maxvalue': return { kind: 'maxvalue' };
      case 'hash': return { kind: 'hash' };
      case 'system': return { kind: 'system' };
    }
  }

  async function handleSubmit() {
    setError(null);
    if (!table) {
      setError('Could not resolve the target table from the parked statement.');
      return;
    }
    const trimmedName = name.trim();
    if (trimmedName === '') { setError('Partition name is required.'); return; }
    const boundary = buildBoundary();
    if ('error' in boundary) { setError(boundary.error); return; }
    const partition: PartitionDef = { name: trimmedName, boundary };
    const validation = validateAddPartitionForEngine(engine, { table, partition });
    if (validation !== null) { setError(validation); return; }
    setPending(true);
    try {
      await commitAddPartition(editContext, { table, partition });
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const showSingleValues = kind === 'range-less-than' || kind === 'list-in';
  const showFromTo = kind === 'range-from-to';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Add partition</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Partition name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="p2025"
          className="h-8 font-mono text-xs"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-boundary-kind`} className="text-xs">Boundary</Label>
        <Select value={kind} onValueChange={(v) => setKind(v as BoundaryKind)}>
          <SelectTrigger
            id={`${testId}-boundary-kind`}
            data-testid={`${testId}-boundary-kind`}
            className="h-8 font-mono text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.kind} value={o.kind}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showSingleValues ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-values`} className="text-xs">
            Values text <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-values`}
            data-testid={`${testId}-values`}
            value={valuesText}
            onChange={(e) => setValuesText(e.target.value)}
            placeholder="2026"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {showFromTo ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${testId}-from`} className="text-xs">FROM</Label>
            <Input
              id={`${testId}-from`}
              data-testid={`${testId}-from`}
              value={fromText}
              onChange={(e) => setFromText(e.target.value)}
              placeholder="2025"
              className="h-8 font-mono text-xs"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${testId}-to`} className="text-xs">TO</Label>
            <Input
              id={`${testId}-to`}
              data-testid={`${testId}-to`}
              value={toText}
              onChange={(e) => setToText(e.target.value)}
              placeholder="2026"
              className="h-8 font-mono text-xs"
            />
          </div>
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>Cancel</Button>
        <Button type="button" size="sm" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
